<script setup>
import {computed, ref} from "vue";

const props = defineProps({
    data: {
        type: Object,
        default: null,
        hidden: true,
    },

    passedSelectedFacets: {
        type: Array,
        default: null,
    },
    selected: [],
});

const showImage = computed(() => true);
const allowedDocumentTypes = computed(() => ['com.liferay.document.library.kernel.model.DLFileEntry', 'Dokument', 'Document', 'Datoteka', 'File']);
const data = computed(() => props.data);
const show = ref(true);
const passedSelectedFacets = computed(() => props.passedSelectedFacets);

const documentType = computed(() => {
    return data.value &&
    Object.prototype.hasOwnProperty.call(data.value, 'documentType') &&
    data.value.documentType &&
    data.value.documentType.length > 0
        ? data.value.documentType
        : null;
});

const datasheetStatus = computed(() => {
    return Object.prototype.hasOwnProperty.call(data.value, 'datasheetStatus')
        ? data.value.datasheetStatus
        : null;
});

const datasheetUrl = computed(() => {
    if (datasheetStatus === 'RESTRICTED') {
        return `/products/request-datasheet?product=${data.value.title}`;
    }

    return data.value.hasOwnProperty('datasheetUrl') && data.value.datasheetUrl.length > 0
        ? data.value.datasheetUrl
        : null;
});

const isDocument = computed(() => {
    return allowedDocumentTypes.value.includes(data.value.type);
});


const resultTarget = computed(() => {
    return isDocument.value ? '_blank' : '_self';
});

const isVideo = computed(() => {
    if (!Object.prototype.hasOwnProperty.call(data.value, 'youtubeUrl')) {
        return false;
    }

    return (data.value.youtubeUrl !== null && data.value.youtubeUrl.length > 0);
});

const videoUrl = computed(() => {
    if (Object.prototype.hasOwnProperty.call(data.value, 'youtubeUrl') && data.value.youtubeUrl !== null) {
        return data.value.youtubeUrl;
    }

    return null;
});

const imageUrl = computed(() => {

    if (data.value.hasOwnProperty('imageUrl') && data.value.imageUrl && data.value.imageUrl.length) {
        if (data.value.imageUrl.includes("https")) {
            return `${data.value.imageUrl}`;
        } else {
            return `/o/imageoptim/resize${data.value.imageUrl}/800`;
        }
    }

    return null;
});

const link = computed(() => {
    if (data.value !== null && data.value !== undefined) {
        if (data.value.hasOwnProperty('targetUrl') && data.value.targetUrl && data.value.targetUrl.length) {
            return data.value.targetUrl;
        } else if (!isVideo && datasheetUrl !== null && datasheetUrl !== undefined && datasheetUrl.length > 0) {
            return datasheetUrl;
        }
        return null;
    }
    return null;
});

const targetLink = computed(() => {

    if (data.value.title === "Beratersuche"){
        return '_blank';
    }

    if (data.value.externalUrl) {
        return '_blank';
    } else if (data.value.hasOwnProperty('targetUrl') && data.value.targetUrl && data.value.targetUrl.length > 0) {
        return resultTarget.value;
    } else {
        return '_blank';
    }
});

</script>

<template>
    <article class="search-result" v-if="data !== null && data !== undefined"
             :class="{'is-document': isDocument, 'has-image' : data.imageUrl && showImage, 'has-video': isVideo }"
             :data-show="show"
    >
        <a v-if="link !== null && link !== undefined && link.length > 0 "
           class="full-result"
           :href="link.replace('-staging','')"
           :target="targetLink">
            <div class="result-preview" v-if="(data.imageUrl && showImage) || isVideo">
                <div v-if="data.imageUrl && showImage" ref="image" class="result-image"
                     :style="{ backgroundImage: `url(${imageUrl})` }">
                </div>

                <div v-if="isVideo && videoUrl.length > 0" class="result-video">
                    <template v-if="videoUrl.length > 0">
                        <iframe :src="videoUrl" height="100%" width="100%" allowfullscreen></iframe>
                    </template>
                </div>
            </div>

            <div class="result-content"
                 :class="{ 'has-description': data.description}">
                <div class="title-bar">
                    <h3 class="title is-4" v-html="data.title"></h3>
                </div>

                <div v-if="data.description && data.description.length" class="result-description">
                    <p ref="content" v-html="data.description"/>
                </div>
                <div class="link" v-if="!isVideo &&  data.targetUrl !== null && data.targetUrl !== undefined">
                    <span v-if="data.targetUrl.length > 0"
                          class="result-link result-button">
                        <span v-if="isDocument" v-html="$t('search.download')"></span>
                        <span v-if="!isDocument" v-html="$t('search.readMore')"></span>

                        <svg v-if="isDocument" class="icon icon-down" width="16px" height="17px">
                            <use xlink:href="#search-icon-down-more"></use>
                        </svg>

                        <span class="more" v-if="!isDocument">
                            <svg class="icon-chevron-right icon icon-right" width="10px" height="10px">
                                <use xlink:href="#icon-chevron-right"></use>
                            </svg>
                        </span>

                    </span>
                </div>
                <div class="link" v-if="datasheetUrl &&  datasheetUrl !== null && datasheetUrl !== undefined">
                    <span v-if="datasheetUrl.length > 0"
                          class="result-link result-button"
                          :href="datasheetUrl"
                          target="_blank">
                        {{ $t('search.downloadDatasheet') }}

                        <svg v-if="isDocument" class="icon icon-down" width="16px" height="17px">
                            <use xlink:href="#search-icon-down-more"></use>
                        </svg>

                        <span class="more" v-if="!isDocument">
                            <svg class="icon-chevron-right icon icon-right" width="10px" height="10px">
                                <use xlink:href="#icon-chevron-right"></use>
                            </svg>
                        </span>
                    </span>
                </div>
            </div>
        </a>
        <div v-else class="full-result">
            <div class="result-preview" v-if="(data.imageUrl && showImage) || isVideo">
                <div v-if="data.imageUrl && showImage" ref="image" class="result-image"
                     :style="{ backgroundImage: `url(${imageUrl})` }">
                </div>

                <div v-if="isVideo && videoUrl.length > 0" class="result-video">
                    <template v-if="videoUrl.length > 0">
                        <iframe :src="videoUrl" height="100%" width="100%" allowfullscreen></iframe>
                    </template>
                </div>
            </div>

            <div class="result-content"
                 :class="{ 'has-description': data.description}">
                <div class="title-bar">
                    <h3 class="title is-4" v-html="data.title"></h3>
                </div>

                <div v-if="data.description && data.description.length" class="result-description">
                    <p ref="content" v-html="data.description"/>
                </div>
                <div class="link" v-if="!isVideo &&  data.targetUrl !== null && data.targetUrl !== undefined">
                    <span v-if="data.targetUrl.length > 0"
                          class="result-link result-button">
                        <span v-if="isDocument" v-html="$t('search.download')"></span>
                        <span v-if="!isDocument" v-html="$t('search.readMore')"></span>

                        <svg v-if="isDocument" class="icon icon-down" width="16px" height="17px">
                            <use xlink:href="#search-icon-down-more"></use>
                        </svg>

                        <span class="more" v-if="!isDocument">
                            <svg class="icon-chevron-right icon icon-right" width="10px" height="10px">
                                <use xlink:href="#icon-chevron-right"></use>
                            </svg>
                        </span>

                    </span>
                </div>
                <div class="link" v-if="datasheetUrl &&  datasheetUrl !== null && datasheetUrl !== undefined">
                    <span v-if="datasheetUrl.length > 0"
                          class="result-link result-button"
                          :href="datasheetUrl"
                          target="_blank">
                        {{ $t('search.downloadDatasheet') }}

                        <svg v-if="isDocument" class="icon icon-down" width="16px" height="17px">
                            <use xlink:href="#search-icon-down-more"></use>
                        </svg>

                        <span class="more" v-if="!isDocument">
                            <svg class="icon-chevron-right icon icon-right" width="10px" height="10px">
                                <use xlink:href="#icon-chevron-right"></use>
                            </svg>
                        </span>
                    </span>
                </div>
            </div>
        </div>
    </article>
</template>
